import React, { useState } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import LinearProgress from "@mui/material/LinearProgress";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckIcon from "@mui/icons-material/Check";
import PersonIcon from "@mui/icons-material/Person";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import LinkIcon from "@mui/icons-material/Link";
import { alpha, useTheme } from "@mui/material";
import { PlanItemObject } from "../utils/backend-utils";

interface SessionDurationsCardProps {
  plan: PlanItemObject[];
  sessionStartedAt?: string;
}

function formatDuration(seconds: number | undefined): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return "—";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
  const hours = Math.floor(seconds / 3600);
  const rem = seconds % 3600;
  return `${hours}h ${Math.floor(rem / 60)}m`;
}

function diffSeconds(a?: string, b?: string): number | undefined {
  if (!a || !b) return undefined;
  const ta = Date.parse(a);
  const tb = Date.parse(b);
  if (Number.isNaN(ta) || Number.isNaN(tb)) return undefined;
  return Math.max(0, Math.round((tb - ta) / 1000));
}

function formatTime(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

type StepStatus = "completed" | "active" | "pending";

function resolveStatus(step: PlanItemObject): StepStatus {
  if (step.status === "COMPLETED" || step.completed_at) return "completed";
  if (step.started_at) return "active";
  return "pending";
}

interface TimelineStepProps {
  step: PlanItemObject;
  index: number;
  isLast: boolean;
  sessionStartedAt?: string;
}

function TimelineStep({ step, index, isLast, sessionStartedAt }: TimelineStepProps) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const status = resolveStatus(step);

  const color =
    status === "completed" ? theme.palette.success.main
    : status === "active" ? theme.palette.primary.main
    : theme.palette.grey[400];

  const Icon =
    status === "completed" ? CheckIcon
    : status === "active" ? PlayArrowIcon
    : AccessTimeIcon;

  const dur =
    step.duration_seconds ??
    diffSeconds(step.started_at, step.completed_at) ??
    (status === "active" ? diffSeconds(step.started_at, new Date().toISOString()) : undefined);

  const isLowConfidence = String(step.confidence || "HIGH").toUpperCase() === "LOW";
  const source = step.source;
  const hasUserOutput = Boolean(step.user_output || step.image_analysis);
  const canExpand = hasUserOutput || Boolean(source) || isLowConfidence;

  return (
    <Box sx={{ display: "flex", gap: 2, position: "relative" }}>
      {/* Left rail: status dot + vertical connector line */}
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            bgcolor: status === "pending" ? "transparent" : color,
            border: `2px solid ${color}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: status === "pending" ? color : theme.palette.getContrastText(color),
            fontWeight: 700,
            fontSize: "0.85rem",
            boxShadow: status === "active" ? `0 0 0 4px ${alpha(color, 0.2)}` : "none",
            zIndex: 1,
          }}
        >
          {status === "completed" ? <Icon fontSize="small" /> : index + 1}
        </Box>
        {!isLast && (
          <Box
            sx={{
              width: 2,
              flex: 1,
              minHeight: 24,
              bgcolor: status === "completed" ? theme.palette.success.main : alpha(theme.palette.divider, 0.6),
            }}
          />
        )}
      </Box>

      {/* Right: step card */}
      <Box sx={{ flex: 1, minWidth: 0, pb: isLast ? 0 : 2 }}>
        <Paper
          elevation={0}
          onClick={canExpand ? () => setExpanded((v: boolean) => !v) : undefined}
          sx={{
            p: 1.5,
            borderRadius: 1.5,
            border: `1px solid ${alpha(color, status === "pending" ? 0.3 : 0.35)}`,
            bgcolor: status === "active" ? alpha(color, 0.06) : theme.palette.background.paper,
            cursor: canExpand ? "pointer" : "default",
            transition: "background-color 0.15s ease, border-color 0.15s ease",
            "&:hover": canExpand ? {
              bgcolor: alpha(color, 0.08),
              borderColor: alpha(color, 0.5),
            } : {},
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                flex: 1,
                fontWeight: status === "active" ? 600 : 500,
                color: status === "pending" ? theme.palette.text.secondary : theme.palette.text.primary,
                minWidth: 0,
                wordBreak: "break-word",
              }}
            >
              <Box component="span" sx={{ color, fontWeight: 700, mr: 1 }}>
                Step {index + 1}
              </Box>
              {step.task}
            </Typography>
            {canExpand && (
              <IconButton
                size="small"
                aria-label={expanded ? "Hide details" : "Show details"}
                onClick={(e: React.MouseEvent) => { e.stopPropagation(); setExpanded((v: boolean) => !v); }}
                sx={{ mt: -0.5 }}
              >
                {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            )}
          </Box>

          {/* Metadata row — small, muted */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap", mt: 0.75 }}>
            {dur != null && (
              <Chip
                size="small"
                icon={<AccessTimeIcon fontSize="small" />}
                label={formatDuration(dur)}
                sx={{ borderRadius: "6px", height: 22, fontSize: "0.7rem" }}
              />
            )}
            {status === "active" && (
              <Chip size="small" variant="outlined" label="in progress" sx={{ borderRadius: "6px", height: 22, fontSize: "0.7rem", borderColor: color, color }} />
            )}
            {step.completed_at && (
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: "0.7rem" }}>
                completed {formatTime(step.completed_at)}
              </Typography>
            )}
            {step.completed_by && step.completed_by !== "UNKNOWN" && (
              <Chip
                size="small"
                variant="outlined"
                icon={<PersonIcon fontSize="small" />}
                label={step.completed_by}
                sx={{ borderRadius: "6px", height: 22, fontSize: "0.7rem", maxWidth: 260 }}
              />
            )}
            {isLowConfidence && (
              <Chip
                size="small"
                icon={<WarningAmberIcon fontSize="small" />}
                label="LOW confidence"
                sx={{
                  borderRadius: "6px",
                  height: 22,
                  fontSize: "0.7rem",
                  bgcolor: alpha(theme.palette.warning.main, 0.15),
                  color: theme.palette.warning.dark,
                  "& .MuiChip-icon": { color: theme.palette.warning.dark },
                }}
              />
            )}
          </Box>

          {/* Expanded details */}
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px dashed ${alpha(theme.palette.divider, 0.5)}` }}>
              {source && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
                  <LinkIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} />
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>
                    Source:
                  </Typography>
                  {source === "GENERAL_KNOWLEDGE" ? (
                    <Typography variant="caption" sx={{ color: theme.palette.warning.dark, fontStyle: "italic" }}>
                      LLM general knowledge (no authoritative doc)
                    </Typography>
                  ) : (
                    <Typography
                      component="a"
                      href={source}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="caption"
                      sx={{ color: theme.palette.primary.main, wordBreak: "break-all", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    >
                      {source}
                    </Typography>
                  )}
                </Box>
              )}
              {step.user_output && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600, display: "block" }}>
                    Your answer:
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                    {step.user_output}
                  </Typography>
                </Box>
              )}
              {step.image_analysis && (
                <Box>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600, display: "block" }}>
                    Image analysis:
                  </Typography>
                  <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                    {step.image_analysis}
                  </Typography>
                </Box>
              )}
              {step.started_at && (
                <Typography variant="caption" sx={{ display: "block", color: theme.palette.text.secondary, mt: 1 }}>
                  Started: {formatTime(step.started_at)}
                  {sessionStartedAt && step.started_at && (
                    <> · {formatDuration(diffSeconds(sessionStartedAt, step.started_at))} into session</>
                  )}
                </Typography>
              )}
            </Box>
          </Collapse>
        </Paper>
      </Box>
    </Box>
  );
}

/**
 * Vertical timeline of session steps, with connected status dots on the left
 * and cards on the right. Clicking a card expands it to show the user's answer,
 * source URL, image analysis, and confidence.
 *
 * Hides itself until at least one step has state (started_at or completed_at)
 * so we don't show an empty timeline during initial editing.
 */
export function SessionDurationsCard({ plan, sessionStartedAt }: SessionDurationsCardProps) {
  const theme = useTheme();
  const anyStarted = plan.some((s) => s.started_at || s.completed_at || s.status === "COMPLETED");
  if (!anyStarted) return null;

  const completedCount = plan.filter((s) => s.status === "COMPLETED" || s.completed_at).length;
  const total = plan.length;
  const pct = total > 0 ? Math.round((completedCount / total) * 100) : 0;

  const totalSeconds = (() => {
    if (!sessionStartedAt) return undefined;
    const lastTs = plan.reduce<string | undefined>((acc, s) => {
      const t = s.completed_at || s.started_at;
      if (!t) return acc;
      if (!acc) return t;
      return t > acc ? t : acc;
    }, undefined);
    if (!lastTs) return undefined;
    return diffSeconds(sessionStartedAt, lastTs);
  })();

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: 2,
        bgcolor: theme.palette.background.paper,
        border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
      }}
    >
      {/* Hero */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1.5, flexWrap: "wrap" }}>
        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1rem", flex: 1, minWidth: 0 }}>
          Session timeline
        </Typography>
        <Chip
          size="small"
          label={`${completedCount} / ${total} steps done`}
          sx={{
            borderRadius: "6px",
            fontWeight: 600,
            bgcolor: completedCount === total ? alpha(theme.palette.success.main, 0.15) : alpha(theme.palette.primary.main, 0.1),
            color: completedCount === total ? theme.palette.success.dark : theme.palette.primary.main,
          }}
        />
        {totalSeconds != null && (
          <Chip
            size="small"
            icon={<AccessTimeIcon fontSize="small" />}
            label={`Total: ${formatDuration(totalSeconds)}`}
            sx={{ borderRadius: "6px", fontWeight: 600 }}
          />
        )}
      </Box>

      {/* Progress bar */}
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: 6,
          borderRadius: 3,
          mb: 2,
          bgcolor: alpha(theme.palette.divider, 0.3),
          "& .MuiLinearProgress-bar": {
            bgcolor: pct === 100 ? theme.palette.success.main : theme.palette.primary.main,
          },
        }}
      />

      {/* Timeline */}
      <Box>
        {plan.map((step, i) => (
          <TimelineStep
            key={String(step.id ?? i)}
            step={step}
            index={i}
            isLast={i === plan.length - 1}
            sessionStartedAt={sessionStartedAt}
          />
        ))}
      </Box>
    </Paper>
  );
}
